import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Stack,
  TableBody,
  Box,
  Button,
  ButtonGroup,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Typography,
  Tooltip,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import useAccess from '../../hooks/access.hook';
import {
  getRulesetScheduleData,
  purgeChannelVideosSchedule,
  generateIncrementalChannelVideosSchedule,
  generateChannelVideosSchedule,
} from '../../services/ruleset.service';
import { getVideosCountForRulesetsByChannel } from '../../services/videos.service';
import { formatDate, getDate } from '../../utils/datetime.util';
import { CONTENT_SCHEDULE_TYPE } from '../../config/const.config';
import tooltipGlossaryInfo from '../../utils/tooltip-info.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import ConfirmPopup from '../common/confirm-popup.component';
import Datepicker from '../form-elements/datepicker.component';
import ViewScheduleRulesetRules from './view-schedule-ruleset-rules.component';
import AddEditScheduleRulesetRules from './add-edit-schedule-ruleset-rules.component';
import ListScheduledVideos from './list-scheduled-videos.component';
import ManageRulesetsOrder from './manage-rulesets-order.component';

const ContentSchedulingRulesets = ({
  channelId,
  activeScheduleType,
  refreshCountsView,
}) => {
  const tooltipeType =
    activeScheduleType === CONTENT_SCHEDULE_TYPE.GENERAL
      ? 'schedule'
      : 'heroPlaylistSchedule';
  const { hasOperationAccess } = useAccess();
  const buttonGroupList = useRef([
    { value: 'incrementalRun', label: 'Incremental Run' },
    { value: 'purgeRun', label: 'Purge and Run' },
    { value: 'purgeOnly', label: 'Purge Only' },
  ]);
  const scheduleInfo = useRef(null);
  const scheduleInfoRows = useRef(null);
  const rulesetsListToDisplay = useRef([]);
  const scheduleType = useRef(activeScheduleType);
  const anchorRef = useRef(null);
  const hasRulesets = useRef(false);
  const viewRulesetElement = useRef(null);
  const rulesetDates = useRef([]);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [refreshView, setRefreshView] = useState(0);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [showButtonGroupList, setShowButtonGroupList] = useState(false);
  const [selectedRunAction, setSelectedRunAction] = useState(null);
  const [selectedRuleset, setSelectedRuleset] = useState(null);
  const [createNewRuleset, setCreateNewRuleset] = useState(false);
  const [filtersDate, setFiltersDate] = useState({
    startDate: getDate(-(2 * 7)),
    endDate: getDate(24 * 7),
  });
  // eslint-disable-next-line no-unused-vars
  const [dataCounters, setDataCounters] = useState(1);
  const [showRulesetsOrder, setShowRulesetsOrder] = useState(false);

  const getConfirmPopupTexts = (type) => {
    const textOptions = {
      incrementalRun: {
        autofillConfirm: false,
        title: 'Generate Schedule - Incremental Run',
        message:
          'Do you want to run incremental schedule? This will check the videos against rules and assign schedule to newly assigned videos.',
      },
      purgeRun: {
        autofillConfirm: false,
        title: 'Generate Schedule - Purge and Run',
        message:
          'Do you want to purge existing schedule and create a fresh schedule? This may impact the existing app, so please be careful.',
      },
      purgeOnly: {
        autofillConfirm: false,
        title: 'Generate Schedule - Purge Only',
        message:
          'Do you want to purge existing schedule? This may impact the existing app, so please be careful.',
      },
    };

    return textOptions[type] || null;
  };

  const prepareDataToDisplay = async () => {
    getVideosCountForRulesetsByChannel(channelId, scheduleType.current)
      .then((res) => {
        for (let i = 0; i < rulesetsListToDisplay.current.length; i += 1) {
          const index = rulesetsListToDisplay.current[i];
          const { rulesetId } = scheduleInfoRows.current[index];

          scheduleInfoRows.current[index].matchedContent = 0;
          scheduleInfoRows.current[index].showLoader = false;

          if (res.data[rulesetId]) {
            scheduleInfoRows.current[index].matchedContent =
              res.data[rulesetId];
            scheduleInfoRows.current[index].showLoader = false;
          }
        }

        setDataCounters((prev) => prev + 1);
        setProcessing(false);
      })
      .catch(() => {
        setProcessing(false);
      });
  };

  const handleRefreshView = () => {
    setCreateNewRuleset(false);
    setShowButtonGroupList(false);
    setSelectedRunAction(null);
    setSelectedRuleset(null);
    setLoading(true);
    setRefreshView((prev) => prev + 1);
  };

  const handleRunActions = () => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    let scheduleFunction = null;
    if (selectedRunAction === 'incrementalRun') {
      scheduleFunction = generateIncrementalChannelVideosSchedule;
    } else if (selectedRunAction === 'purgeRun') {
      scheduleFunction = generateChannelVideosSchedule;
    } else if (selectedRunAction === 'purgeOnly') {
      scheduleFunction = purgeChannelVideosSchedule;
    }

    scheduleFunction(channelId, scheduleType.current)
      .then(() => {
        refreshCountsView();
        handleRefreshView();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setSelectedRunAction(null);
      });
  };

  const handleRulesetsOrderSuccess = () => {
    handleRefreshView();
    setShowRulesetsOrder(false);
  };

  const handleSelectRuleset = (val) => {
    if (Number(val) > 0) {
      setSelectedRuleset(val);

      if (viewRulesetElement.current) {
        const element = viewRulesetElement.current;
        const headerOffset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    } else {
      setSelectedRuleset(null);
    }
  };

  const handleInvokeSnackbar = (message = '', messageType = 'success') => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    if (message) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: messageType,
          message,
        });
      }, 50);
    }
  };

  useEffect(() => {
    setSnackbarInfo({ ...snackbarInfo, show: false });
    scheduleInfo.current = null;
    scheduleInfoRows.current = {};
    rulesetsListToDisplay.current = [];
    rulesetDates.current = [];

    let params = `?type=${scheduleType.current}`;
    if (filtersDate.startDate) {
      params += `&startDate=${formatDate(filtersDate.startDate, 'YYYY-MM-DD')}`;
    }
    if (filtersDate.endDate) {
      params += `&endDate=${formatDate(filtersDate.endDate, 'YYYY-MM-DD')}`;
    }
    getRulesetScheduleData(channelId, params)
      .then((res) => {
        scheduleInfo.current = { ...res.data.info };
        scheduleInfoRows.current = { ...res.data.rows };
        rulesetDates.current = [...res.data.schedule];
        rulesetsListToDisplay.current = scheduleInfo.current.rulesets || [];
        hasRulesets.current = (scheduleInfo.current.rulesets || []).length > 3;

        prepareDataToDisplay();
        setLoading(false);
      })
      .catch(() => {
        setSnackbarInfo({
          show: true,
          type: 'error',
          message: 'Something went wrong, please try again.',
        });
      });
  }, [refreshView]);

  return (
    <Paper variant="outlined" square sx={{ p: 1 }}>
      {!loading && scheduleInfo.current && (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 1 }}
          >
            <Box sx={{ display: 'flex' }}>
              <Datepicker
                id="start_date"
                name="start_date"
                label="From Date"
                defaultValue={filtersDate.startDate}
                clearable
                onChange={(v) => {
                  setFiltersDate({
                    ...filtersDate,
                    startDate: v,
                  });
                  setRefreshView((prev) => prev + 1);
                }}
                sx={{ p: 0 }}
              />
              <Box>&nbsp;&nbsp;&nbsp;</Box>
              <Datepicker
                id="end_date"
                name="end_date"
                label="To Date"
                defaultValue={filtersDate.endDate}
                clearable
                onChange={(v) => {
                  setFiltersDate({
                    ...filtersDate,
                    endDate: v,
                  });
                  setRefreshView((prev) => prev + 1);
                }}
              />
            </Box>
            <Box>
              {hasOperationAccess('rule_based_scheduling', 'manage') && (
                <>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => setShowRulesetsOrder(true)}
                    sx={{ mr: 1 }}
                  >
                    Change Rulesets Order
                  </Button>

                  <ButtonGroup
                    variant="contained"
                    ref={anchorRef}
                    aria-label="schedule-generate"
                    size="small"
                    disabled={!hasRulesets.current}
                    sx={{ mr: 1 }}
                  >
                    <Button
                      onClick={() =>
                        setShowButtonGroupList(!showButtonGroupList)
                      }
                    >
                      Generate Schedule
                    </Button>
                    <Button
                      aria-controls="drop-down-icon"
                      aria-expanded
                      aria-label="drop-down-ico"
                      aria-haspopup="menu"
                      onClick={() =>
                        setShowButtonGroupList(!showButtonGroupList)
                      }
                    >
                      <ArrowDropDownIcon />
                    </Button>
                  </ButtonGroup>
                  <Popper
                    open={showButtonGroupList}
                    anchorEl={anchorRef.current}
                    transition
                    disablePortal
                    sx={{ zIndex: 3 }}
                  >
                    {({ TransitionProps, placement }) => (
                      <Grow
                        {...TransitionProps}
                        style={{
                          transformOrigin:
                            placement === 'bottom'
                              ? 'center top'
                              : 'center bottom',
                        }}
                      >
                        <Paper
                          style={{
                            width: anchorRef.current?.offsetWidth || undefined,
                          }}
                          elevation={3}
                        >
                          <ClickAwayListener
                            onClickAway={() => setShowButtonGroupList(false)}
                          >
                            <MenuList
                              id="schedule-generate-button-menu"
                              autoFocusItem
                            >
                              {buttonGroupList.current.map((btn, index) => (
                                <MenuItem
                                  key={index}
                                  onClick={() => {
                                    setSelectedRunAction(btn.value);
                                  }}
                                >
                                  <Typography variant="body2">
                                    {btn.label}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </>
              )}

              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:twotone-refresh" />}
                onClick={handleRefreshView}
                disabled={false}
              >
                Refresh
              </Button>
            </Box>
          </Stack>

          <Stack sx={{ p: 1 }}>
            <TableContainer
              component={Paper}
              variant="outlined"
              square
              sx={{ overflowX: 'auto' }}
            >
              <Table
                size="small"
                aria-label="a dense table"
                stickyHeader
                sx={{
                  minWidth: 700,
                  tableLayout: 'fixed',
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      width={250}
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                      }}
                    >
                      Ruleset Name
                    </TableCell>
                    <TableCell
                      align="right"
                      width={150}
                      sx={{
                        position: 'sticky',
                        left: 250,
                        zIndex: 2,
                      }}
                    >
                      <Tooltip
                        title={
                          tooltipGlossaryInfo[tooltipeType].matchedVideosFound
                        }
                        arrow
                        placement="left-start"
                      >
                        Matched Videos Found
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      align="right"
                      width={100}
                      sx={{
                        position: 'sticky',
                        left: 400,
                        zIndex: 2,
                      }}
                    >
                      <Tooltip
                        title={
                          tooltipGlossaryInfo[tooltipeType].scheduledVideos
                        }
                        arrow
                        placement="left-start"
                      >
                        Scheduled Videos
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      align="right"
                      width={100}
                      sx={{
                        position: 'sticky',
                        left: 500,
                        zIndex: 2,
                      }}
                    >
                      <Tooltip
                        title={tooltipGlossaryInfo[tooltipeType].liveVideos}
                        arrow
                        placement="left-start"
                      >
                        Live Videos
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      align="right"
                      width={100}
                      sx={{
                        position: 'sticky',
                        left: 600,
                        zIndex: 2,
                      }}
                    >
                      <Tooltip
                        title={tooltipGlossaryInfo[tooltipeType].notLiveVideos}
                        arrow
                        placement="left-start"
                      >
                        Not Live Videos
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      align="right"
                      width={100}
                      sx={{
                        position: 'sticky',
                        left: 700,
                        zIndex: 2,
                      }}
                    >
                      <Tooltip
                        title={tooltipGlossaryInfo[tooltipeType].weeksScheduled}
                        arrow
                        placement="left-start"
                      >
                        Weeks Scheduled
                      </Tooltip>
                    </TableCell>
                    {rulesetDates.current.map((d, idx) => (
                      <TableCell
                        align="right"
                        width={100}
                        sx={{ zIndex: 1 }}
                        key={`hc-${d.date}-${idx}`}
                      >
                        {formatDate(d.date, 'MM/DD/YY')}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rulesetsListToDisplay.current.map((rs) => (
                    <TableRow
                      key={`ruleset-${rs}`}
                      sx={{
                        backgroundColor: 'white',
                        ...(Number(
                          scheduleInfoRows.current[rs].rulesetId || 0
                        ) > 0 && {
                          cursor: 'pointer',
                          backgroundColor:
                            selectedRuleset === rs ? '#FFE9E8' : 'white',
                          '&:hover': selectedRuleset !== rs && {
                            background: '#FFF4F4',
                          },
                        }),
                      }}
                      onClick={() => {
                        handleSelectRuleset(rs);
                      }}
                    >
                      <TableCell
                        sx={{
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: 'inherit',
                        }}
                      >
                        {scheduleInfoRows.current[rs].rulesetName}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          position: 'sticky',
                          left: 250,
                          zIndex: 2,
                          backgroundColor: 'inherit',
                        }}
                      >
                        {scheduleInfoRows.current[rs].showLoader ? (
                          <Iconify icon="line-md:loading-loop" />
                        ) : (
                          scheduleInfoRows.current[rs].matchedContent
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          position: 'sticky',
                          left: 400,
                          zIndex: 2,
                          backgroundColor: 'inherit',
                        }}
                      >
                        {scheduleInfoRows.current[rs].scheduledContent}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          position: 'sticky',
                          left: 500,
                          zIndex: 2,
                          backgroundColor: 'inherit',
                        }}
                      >
                        {scheduleInfoRows.current[rs].liveContent}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          position: 'sticky',
                          left: 600,
                          zIndex: 2,
                          backgroundColor: 'inherit',
                        }}
                      >
                        {scheduleInfoRows.current[rs].notLiveContent}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          position: 'sticky',
                          left: 700,
                          zIndex: 2,
                          backgroundColor: 'inherit',
                        }}
                      >
                        {scheduleInfoRows.current[rs].numWeekScheduled}
                      </TableCell>
                      {rulesetDates.current.map((d, idx) => (
                        <TableCell
                          align="right"
                          width={100}
                          sx={{ zIndex: 1 }}
                          key={`hcv-${d.date}-${idx}`}
                        >
                          {scheduleInfoRows.current[rs][d.date] || '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {hasOperationAccess('rule_based_scheduling', 'manage') && (
              <Box sx={{ my: 1 }}>
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={processing}
                  onClick={() => {
                    setCreateNewRuleset(true);
                  }}
                >
                  Add New Ruleset
                </Button>
              </Box>
            )}
          </Stack>

          <Stack sx={{ p: 1 }} ref={viewRulesetElement}>
            {selectedRuleset && (
              <Paper variant="outlined" sx={{ p: 1 }}>
                <ViewScheduleRulesetRules
                  dataId={Number(selectedRuleset)}
                  rulesetType="SCHEDULING"
                  showSnackbarMessage={handleInvokeSnackbar}
                  handleRefreshData={() => {
                    refreshCountsView();
                    handleRefreshView();
                  }}
                  channelContentScheduleInfoId={scheduleInfo.current.id}
                />
              </Paper>
            )}

            <Paper elevation={0} sx={{ p: 1 }}>
              <ListScheduledVideos
                title=""
                channelContentScheduleInfoId={scheduleInfo.current.id}
                channelId={channelId}
                activeScheduleType={activeScheduleType}
              />
            </Paper>
          </Stack>
        </>
      )}

      {!loading && createNewRuleset && scheduleInfo.current && (
        <AddEditScheduleRulesetRules
          title="Add New Ruleset"
          channelContentScheduleInfoId={scheduleInfo.current.id}
          dataId={0}
          rulesetType="SCHEDULING"
          channelId={channelId}
          onSave={() => {
            refreshCountsView();
            handleRefreshView();
          }}
          onCancel={() => {
            setCreateNewRuleset(false);
          }}
          showSnackbarMessage={handleInvokeSnackbar}
          isHeroScheduling={scheduleType.current === CONTENT_SCHEDULE_TYPE.HERO}
        />
      )}

      {!loading && selectedRunAction && scheduleInfo.current && (
        <ConfirmPopup
          title={getConfirmPopupTexts(selectedRunAction).title}
          message={getConfirmPopupTexts(selectedRunAction).message}
          onCancel={() => setSelectedRunAction(null)}
          onSuccess={() => handleRunActions()}
          successButtonText="Confirm"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="confirm"
          autofillConfirmation={
            getConfirmPopupTexts(selectedRunAction).autofillConfirm
          }
        />
      )}

      {!loading && showRulesetsOrder && (
        <ManageRulesetsOrder
          title="Reorder Rulesets"
          onCancel={() => setShowRulesetsOrder(false)}
          onSuccess={() => handleRulesetsOrderSuccess()}
          scheduleInfoId={scheduleInfo.current.id}
          scheduleInfoRows={scheduleInfoRows.current}
          rulesetsListToDisplay={rulesetsListToDisplay.current}
        />
      )}

      {loading && <Loading />}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Paper>
  );
};

ContentSchedulingRulesets.propTypes = {
  channelId: PropTypes.number.isRequired,
  activeScheduleType: PropTypes.string.isRequired,
  refreshCountsView: PropTypes.func.isRequired,
};

export default ContentSchedulingRulesets;
